import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import ScrollView from '@/components/Common/ScrollView';
import { SectionTitle } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import DnDFileUploader from '@/features/CsvImport/DnDFileUploader';
import FieldTable, { type DocumentRow } from '@/features/CsvImport/FieldTable';
import UploadPreview, {
  type SimpleCol,
  type SimpleRow,
  UPLOAD_STATE,
} from '@/features/CsvImport/UploadPreview';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import useToastStore from '@/stores/pure/ToastStore';
import { getTimezoneDayjs } from '@/utils/time';
import { track } from '@/utils/track/track';

const ESSENTIALFIELDS = ['contact_name'];

type Props = {
  closeUploadModal: () => void;
};

type FieldDocData = SimpleCol & DocumentRow;

const columns: FieldDocData[] = [
  {
    columnKey: 'contact_name',
    colWidth: 400,
    type: 'string',
    desc: 'The name of the contact.',
  },
  {
    columnKey: 'account',
    colWidth: 200,
    type: 'string',
    desc: 'The name of the company. If no company key is set, then if a company with the same name exists in the Syncly, it will be mapped to that company.',
  },
  {
    columnKey: 'job_function',
    colWidth: 200,
    type: 'string',
    desc: 'The specific role or responsibility within the organization of the Contact.',
  },
  {
    columnKey: 'email',
    colWidth: 200,
    type: 'string',
    desc: 'Email address of the contact.',
  },
  {
    columnKey: 'phone_number',
    colWidth: 200,
    type: 'string',
    desc: "The contact's phone number.",
  },
  {
    columnKey: 'description',
    colWidth: 200,
    type: 'string',
    desc: 'The contact’s description.',
  },
  {
    columnKey: 'reference_link',
    colWidth: 200,
    type: 'string',
    desc: 'The contact-associated URL for quick access to additional information.',
  },
  {
    columnKey: 'type',
    colWidth: 160,
    type: 'string',
    desc: 'A custom attribute of the contact. (e.g., partner, reseller, vendor, or other).',
  },
  {
    columnKey: 'stage',
    colWidth: 160,
    type: 'string',
    desc: 'A custom attribute (stage) of the contact.',
  },
  {
    columnKey: 'ID',
    colWidth: 160,
    type: 'string',
    desc: 'The unique identifier assigned to the contact that originates from an external source.',
  },
  {
    columnKey: 'SNS_ID',
    colWidth: 160,
    type: 'string',
    desc: 'The unique identifier assigned to the contact on Social Networking Services (SNS).',
  },
  {
    columnKey: 'usage',
    colWidth: 160,
    type: 'string',
    desc: 'The quantifiable measurement that tracks the level of engagement with the contact.',
  },
  {
    columnKey: 'agent_email',
    colWidth: 200,
    type: 'string',
    desc: 'The email of the agent.',
  },
  {
    columnKey: 'monthly_spend',
    colWidth: 160,
    type: 'number',
    desc: 'The monthly monetary value of the contract subscribed by the contact.',
  },
  {
    columnKey: 'plan',
    colWidth: 160,
    type: 'string',
    desc: 'The spending tier or subscription level (e.g., Basic, Pro, or Enterprise).',
  },
  {
    columnKey: 'date',
    colWidth: 160,
    type: 'string',
    desc: 'The date which represents a specific event or timestamp associated with the contact.',
  },
];

const CustomerUploadBody = (props: Props) => {
  const color = useColor();
  const { addDefaultToast } = useToastStore((state) => ({
    addDefaultToast: state.addDefaultToast,
  }));

  const [errorMsg, setErrorMsg] = useState('');
  const [mountComplete, setMountComplete] = useState(false);
  const [uploading, setUploading] = useState<UPLOAD_STATE>(UPLOAD_STATE.READY);
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState({
    current: 1,
    total: 100,
  });
  const [formattedData, setFormattedData] = useState<csvCustomerParams[]>([]);

  const onComplete = () => {
    setMountComplete(true);
    addDefaultToast('Successfully Completed', 'success');
  };

  const onFail = () => {
    setMountComplete(false);
    addDefaultToast('Upload Fail', 'fail');
  };

  const uploadData = async (
    data: csvCustomerParams[],
    fileFields: string[],
  ) => {
    try {
      ESSENTIALFIELDS.forEach((field) => {
        if (!fileFields?.includes(field)) {
          throw new Error(
            'Some of your data is formatted in a way we are not familiar with. Check the headers and try again!',
          );
        }
      });

      setFormattedData(
        data.map((d) => ({
          ...d,
          date: d.date ? getTimezoneDayjs(d.date).toJSON() : undefined,
        })),
      );

      setErrorMsg('');
      onComplete();
    } catch (e: any) {
      setErrorMsg(e.message);
      onFail();
    }
  };

  const confirmUpload = async () => {
    setUploading(UPLOAD_STATE.UPLOADING);
    setProgress((state) => ({
      ...state,
      total: formattedData.length,
    }));
    try {
      const chunkSize = 100;
      for (let i = 0; i < formattedData.length; i += chunkSize) {
        const chunk = formattedData.slice(i, i + chunkSize);
        const param = chunk.map((c) => ({
          ...c,
          monthly_spend: Number(c.monthly_spend),
        }));
        await service.contact.uploadCustomers(param);
        setProgress((state) => ({
          ...state,
          current: state.current + chunkSize,
        }));
      }
      setUploading(UPLOAD_STATE.COMPLETE);

      // Track customer upload complete event
      track(AMP_KEYS.CUSTOMER_UPLOAD_COMPLETE, {
        customer_count: formattedData.length,
        upload_type: 'csv',
      });

      addDefaultToast('The file has been successfully uploaded', 'success');
    } catch (e) {
      console.error(e);
      setUploading(UPLOAD_STATE.READY);
      addDefaultToast('Upload Fail', 'fail');
    }
  };

  const previewData: SimpleRow[] = useMemo(() => {
    return formattedData.slice(0, 100).map((row, index) => {
      return {
        rowKey: index,
        rowData: Object.entries(row).map(([key, value]) => {
          return {
            columnKey: key,
            rawData: value?.toString() || '',
          };
        }),
      };
    });
  }, [formattedData]);

  if (mountComplete)
    return (
      <UploadPreview
        columns={columns}
        total={formattedData.length}
        datas={previewData}
        progress={progress}
        uploading={uploading}
        confirmUpload={confirmUpload}
        closeUploadModal={props.closeUploadModal}
      />
    );
  return (
    <ScrollView style={{ height: '100%' }}>
      <Wrapper g={20}>
        {errorMsg && (
          <Typo weight="sb" color={color.red600} style={{ height: 24 }}>
            {errorMsg}
          </Typo>
        )}
        <VFlex
          g={16}
          padding="20px 24px"
          style={{ backgroundColor: color.gray200, borderRadius: 8 }}
        >
          <Typo type="body2" color={color.gray700}>
            You can upload existing contacts using a csv or excel file.
            <br />
            See the table below for information on the contact fields you can
            upload.
          </Typo>
          <a
            href="/assets/sample_contact.csv"
            download="Syncly Contact CSV import Sample.csv"
            target="_self"
            style={{ width: 'fit-content' }}
          >
            <Button
              variant="outlined"
              sizeType="m"
              style={{ width: 'fit-content' }}
            >
              Download sample CSV
            </Button>
          </a>
        </VFlex>
        <DnDFileUploader
          uploadData={uploadData}
          dragActive={dragActive}
          setDragActive={setDragActive}
        />
        <VFlex g={8}>
          <SectionTitle>Contact field description</SectionTitle>
          <FieldTable rows={columns} />
        </VFlex>
      </Wrapper>
    </ScrollView>
  );
};

export default CustomerUploadBody;

const Wrapper = styled(VFlex)`
  padding: 36px 32px;
`;
